import DateRangeFilter from "./date_range_filter_controller";
import {put} from "@rails/request.js";
import * as bootstrap from "bootstrap";

// Connects to data-controller="datatable"
export default class extends DateRangeFilter {
  static targets = ["table", "emptyMsg", "searchInput"];

  static values = {
    search: String,
    total: String,
    sourceUrl: String,
    showPagination: {type: Boolean, default: true}
  };

  connect() {
    if (this.hasTableTarget) {
      this.dataTable = $(this.tableTarget).DataTable(this.dataTableOptions);
    }
    super.connect()
  }

  disconnect() {
    this.dataTable.destroy();
  }

  manipulateData(data) {
    return data;
  }

  // Custom search input handler — works at all viewports. Subclasses can
  // override to add side effects (e.g., swapping empty-state copy when
  // a query is active). Wire up by adding to your view:
  //   <input data-{controller-name}-target="searchInput"
  //          data-action="input->{controller-name}#runSearch">
  // Debounces 250ms before calling dataTable.search().draw().
  runSearch(event) {
    const query = event.target.value
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      if (this.dataTable) {
        this.dataTable.search(query).draw()
      }
    }, 250)
  }

  get source() {
    return this.sourceUrlValue
  }

  get dataTableOptions() {
    let that = this;
    return {
      responsive: true,
      autoWidth: false,
      processing: true,
      serverSide: true,
      ajax: {
        url: this.source, // it is the url that we give in view like this data-source="<%= root_path(format: :json) %>"
        data: this.manipulateData.bind(this),
      },
      // below are the option of the datatable
      pageLength: this.pageLength,
      order: this.order,
      ordering: this.ordering,
      searching: this.searching,
      columns: this.columns,
      lengthChange: this.lengthChange,
      pagingType: "simple_numbers",
      language: {
        searchPlaceholder: this.searchPlaceholder,
        sInfo: `${this.totalValue == "" ? "Total" : this.totalValue} _TOTAL_`,
      },

      drawCallback: function (settings) {
        //check if there is any data is present in the table
        const api = this.api();
        const $table = $(api.table().node());
        const $row = $table.find("tbody tr .dt-empty");
        const isEmpty = $row.length > 0;

        // Keep the column-header row in sync with emptiness: with no data the
        // <thead> reads as a broken/half-loaded table, so hide it when empty
        // (and the empty-state message is shown) and restore it when rows exist.
        if (that.hasEmptyMsgTarget) {
          const $thead = $table.find("thead");
          if (isEmpty) {
            $thead.addClass("d-none");
          } else {
            $thead.removeClass("d-none");
          }
        }

        if (that.showPaginationValue) {
          if (isEmpty) {
            $row.hide();
            that.paginationRow(that.element).classList.add("d-none");
            if (that.hasEmptyMsgTarget) {
              that.emptyMsgTarget.classList.remove("d-none");
            }
          } else {
            that.paginationRow(that.element).classList.remove("d-none");
            if (that.hasEmptyMsgTarget) {
              that.emptyMsgTarget.classList.add("d-none");
            }
          }
        } else {
          that.paginationRow(that.element).classList.add("d-none");
        }

        enableTooltip()
        
        // Reinitialize dropdowns with proper positioning after table redraw
        that.initializeDropdowns()
      },
    };
  }

  paginationRow(element) {
    let allRows = element.querySelectorAll(".dt-layout-row");
    return allRows[allRows.length - 1];
  }

  get ordering() {
    return true;
  }

  get pageLength() {
    return 10;
  }

  get order() {
    return [[0, "asc"]];
  }

  get searching() {
    return true;
  }

  get columns() {
    return [];
  }

  get lengthChange() {
    return false;
  }

  get searchPlaceholder() {
    return this.searchValue || "Search";
  }

  updatePageNumber(pageNumber) {
    if (this.dataTable) {
      this.dataTable.page(pageNumber - 1).draw(false); // DataTables uses zero-based indexing for pages
    }
  }

  get currentPageNumber() {
    if (this.dataTable) {
      return this.dataTable.page() + 1; // DataTables uses zero-based indexing for pages
    }
    return null;
  }

  async toggleSwitch() {
    let currentElement = event.currentTarget;

    let status = currentElement.dataset.statusValue == "true" ? false : true;
    let url = currentElement.dataset.urlValue;
    const switchButton = currentElement;

    const response = await put(url, {
      contentType: "application/json",
      body: {
        status: status,
      },
      responseKind: "turbo-stream",
    });

    if (response.ok) {
      switchButton.classList.toggle("toggle-btn-on");
      currentElement.setAttribute("data-status-value", `${status}`);
    }
  }

  getCurrentPageRemainingRecords() {
    if (this.dataTable) {
      const info = this.dataTable.page.info();
      const currentPage = info.page; // zero-based
      const pageLength = info.length;
      const start = info.start;
      const total = info.recordsDisplay;

      // Calculate remaining records on current page
      const recordsOnCurrentPage = Math.min(pageLength, total - start);

      return recordsOnCurrentPage;
    }
    return 0;
  }

  initializeDropdowns() {
    // Initialize Bootstrap dropdowns with proper boundary configuration to escape overflow containers
    const dropdownElements = this.element.querySelectorAll('.dropdown-toggle[data-bs-toggle="dropdown"]');
    dropdownElements.forEach(toggle => {
      // Dispose existing dropdown instance if any
      const existingDropdown = bootstrap.Dropdown.getInstance(toggle);
      if (existingDropdown) {
        existingDropdown.dispose();
      }
      
      // Create new dropdown with fixed positioning strategy to escape overflow containers
      new bootstrap.Dropdown(toggle, {
        boundary: 'viewport',
        popperConfig: {
          strategy: 'fixed'
        }
      });
    });
  }
}
